import { useEffect } from 'react';

import {
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  PayCalendarSalaryAllowance,
  Individual,
  CheckOutline,
  DropdownOption,
  DropdownInput,
  useToast,
  CloseCircleOutline as CloseCircle,
} from '@aslanpay/portal-common-lib';

import {
  createAllowancePercentageOptions,
  odpAllowancePercentageValues,
} from 'app/constants/individual';

import useFetchIndividual from 'app/hooks/useFetchIndividual';
import useUpdateIndividual from 'app/hooks/useUpdateIndividual';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useFetchIndividualConfiguration from 'app/hooks/useFetchIndividualConfiguration';

import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';

import { createEditSalaryAllowanceSchema, EditSalaryAllowanceFormData } from 'app/utils/schemas';

import { EditSalaryAllowanceModalProps } from './types';

// Extended Individual type that includes allowance fields
interface IndividualWithAllowance extends Individual {
  globalAllowancePercentage?: PayCalendarSalaryAllowance;
}

function EditSalaryAllowanceModal({ id, isOpen, onClose, onEdit }: EditSalaryAllowanceModalProps) {
  const toast = useToast();

  const { data: employee, isLoading: isLoadingEmployee } = useFetchIndividual(id, isOpen) as {
    data: IndividualWithAllowance | undefined;
    isLoading: boolean;
  };

  const { data: payCalendar, isLoading: isLoadingPayCalendar } = useFetchPayCalendar(
    employee?.payCalendarId
  );

  const { data: companyConfig } = useFetchCompanyConfiguration(employee?.companyId || '');
  const { data: individualConfig, error: individualConfigError } = useFetchIndividualConfiguration(
    employee?.id
  );

  // Check both company-level and individual-level configuration
  // If individual config fails (403), fall back to company config only
  const isOnDemandPayEnabled =
    companyConfig?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED' &&
    (individualConfig?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED' ||
      individualConfigError);

  // Show ODP section if module is enabled OR if there's ODP allowance data available
  const hasOdpAllowanceData =
    employee?.odpAllowancePercentage !== undefined ||
    payCalendar?.odpAllowancePercentage !== undefined;

  const shouldShowOdpSection = isOnDemandPayEnabled || hasOdpAllowanceData;

  // Form setup
  const validationSchema = createEditSalaryAllowanceSchema(shouldShowOdpSection as boolean);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EditSalaryAllowanceFormData>({
    resolver: yupResolver(validationSchema) as Resolver<EditSalaryAllowanceFormData>,
    mode: 'onChange',
    defaultValues: {
      globalAllowancePercentageType: '5', // Default to 5%
      globalAllowancePercentageCustom: '',
      odpAllowancePercentageType: '5', // Default to 5%
      odpAllowancePercentageCustom: '',
    },
  });

  const watchedValues = watch();

  // Debug form validation state
  const hasErrors = Object.keys(errors).length > 0;
  const isFormValid =
    !hasErrors &&
    watchedValues.globalAllowancePercentageType &&
    watchedValues.odpAllowancePercentageType &&
    (watchedValues.globalAllowancePercentageType !== 'CUSTOM' ||
      watchedValues.globalAllowancePercentageCustom) &&
    (watchedValues.odpAllowancePercentageType !== 'CUSTOM' ||
      watchedValues.odpAllowancePercentageCustom);

  // Helper function to get current value for display
  const getCurrentGlobalValue = () => {
    const currentValue =
      employee?.globalAllowancePercentage ??
      payCalendar?.globalAllowancePercentage ??
      payCalendar?.odpAllowancePercentage;

    if (currentValue === undefined) return '';
    return `${currentValue}%`;
  };

  const getCurrentOdpValue = () => {
    const currentValue = employee?.odpAllowancePercentage ?? payCalendar?.odpAllowancePercentage;

    if (currentValue === undefined) return '';
    return `${currentValue}%`;
  };

  // Reset form when employee data changes
  useEffect(() => {
    if (employee && payCalendar) {
      const globalValue =
        employee?.globalAllowancePercentage ??
        payCalendar?.globalAllowancePercentage ??
        payCalendar?.odpAllowancePercentage;
      const odpValue = employee?.odpAllowancePercentage ?? payCalendar?.odpAllowancePercentage;

      // Check if values are in predefined options or custom
      const isGlobalCustom =
        globalValue !== undefined &&
        !odpAllowancePercentageValues.includes(globalValue as PayCalendarSalaryAllowance);
      const isOdpCustom =
        odpValue !== undefined &&
        !odpAllowancePercentageValues.includes(odpValue as PayCalendarSalaryAllowance);

      let globalType = '5';
      if (isGlobalCustom) {
        globalType = 'CUSTOM';
      } else if (globalValue !== undefined) {
        globalType = globalValue.toString();
      }

      let odpType = '5';
      if (isOdpCustom) {
        odpType = 'CUSTOM';
      } else if (odpValue !== undefined) {
        odpType = odpValue.toString();
      }

      reset({
        globalAllowancePercentageType: globalType,
        globalAllowancePercentageCustom: isGlobalCustom ? globalValue?.toString() || '' : '',
        odpAllowancePercentageType: odpType,
        odpAllowancePercentageCustom: isOdpCustom ? odpValue?.toString() || '' : '',
      });
    }
  }, [employee, payCalendar, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: updateAllowancePercentages, isLoading: isUpdatingAllowancePercentages } =
    useUpdateIndividual({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Success',
          description: 'The allowance limits for the employee were updated!',
        });

        onEdit();
        handleClose();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description:
            'Sorry, it was not possible to edit the allowance limits for the employee. Please try again.',
        });
      },
    });

  const onSubmit = (data: EditSalaryAllowanceFormData) => {
    if (employee) {
      // Calculate final values to send
      // If CUSTOM is selected, use the custom value; otherwise use the selected predefined value
      const sendGlobalAllowancePercentage =
        data.globalAllowancePercentageType === 'CUSTOM'
          ? parseInt(data.globalAllowancePercentageCustom || '0', 10) || 0
          : parseInt(data.globalAllowancePercentageType || '0', 10) || 0;

      const sendOdpAllowancePercentage =
        data.odpAllowancePercentageType === 'CUSTOM'
          ? parseInt(data.odpAllowancePercentageCustom || '0', 10) || 0
          : parseInt(data.odpAllowancePercentageType || '0', 10) || 0;

      // Create the update request with explicit fields
      const updateRequest = {
        id: employee.id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        additionalEmail: employee.additionalEmail,
        role: employee.role,
        phoneNumber: employee.phoneNumber,
        externalPayrollId: employee.externalPayrollId,
        annualGrossSalary: employee.annualGrossSalary,
        globalAllowancePercentage: sendGlobalAllowancePercentage, // Remove type casting
        odpAllowancePercentage: sendOdpAllowancePercentage, // Remove type casting
        payCalendarId: employee.payCalendarId,
        startDate: employee.startDate,
        allowTracking: employee.allowTracking,
        allowMarketing: employee.allowMarketing,
        canTopUpCompanyPoints: employee.canTopUpCompanyPoints,
        salaried: employee.salaried,
      };

      updateAllowancePercentages(updateRequest);
    }
  };

  const handleChangeGlobalAllowanceType = (value: string | number) => {
    setValue('globalAllowancePercentageType', value.toString(), { shouldValidate: true });
    // Clear custom value when changing type
    if (value !== 'CUSTOM') {
      setValue('globalAllowancePercentageCustom', '', { shouldValidate: true });
    }
    // Trigger validation on ODP field since it depends on global value
    setValue('odpAllowancePercentageType', watchedValues.odpAllowancePercentageType || '5', {
      shouldValidate: true,
    });
  };

  const handleChangeOdpAllowanceType = (value: string | number) => {
    setValue('odpAllowancePercentageType', value.toString(), { shouldValidate: true });
    // Clear custom value when changing type
    if (value !== 'CUSTOM') {
      setValue('odpAllowancePercentageCustom', '', { shouldValidate: true });
    }
  };

  const handleCustomGlobalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('globalAllowancePercentageCustom', e.target.value, { shouldValidate: true });
    // Trigger validation on ODP field since it depends on global value
    setValue('odpAllowancePercentageType', watchedValues.odpAllowancePercentageType || '5', {
      shouldValidate: true,
    });
  };

  const handleCustomOdpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('odpAllowancePercentageCustom', e.target.value, { shouldValidate: true });
  };

  const allowancePercentageOptions: DropdownOption[] = createAllowancePercentageOptions();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text as="h6">Edit the employees allowance limits</Text>
              <Spacer />
              <IconButton
                variant="link"
                icon={<CloseCircle boxSize="30px" />}
                aria-label="Edit employee allowance limits"
                onClick={handleClose}
              />
            </HStack>
          </ModalHeader>
          {isLoadingEmployee || isLoadingPayCalendar ? (
            <Center width="full" height="full" flexGrow={1}>
              <Spinner color="brand.purple" size="xl" mb="50px" />
            </Center>
          ) : (
            <ModalBody mt="20px">
              <Stack spacing="30px">
                {/* Global limit section */}
                <Flex gap="10px" flexDir="column">
                  <Text color="brand.mutedPurple" textStyle="body">
                    Global limit (%) - Current
                  </Text>
                  <Text as="h6" color="brand.mutedPurple" textStyle="h6SemiBold">
                    {getCurrentGlobalValue()}
                  </Text>
                </Flex>
                <Flex gap="6px" flexDir="column">
                  <Text color="brand.darkGray" textStyle="extraSmallRegular">
                    Global limit (%) - New
                  </Text>
                  <DropdownInput
                    placeholder="Select a percentage"
                    defaultValue={allowancePercentageOptions.find(
                      (option) => option.value === watchedValues.globalAllowancePercentageType
                    )}
                    options={allowancePercentageOptions}
                    onChange={handleChangeGlobalAllowanceType}
                    onBlur={() => {}}
                  />
                  {errors.globalAllowancePercentageType && (
                    <Text color="system.error" textStyle="extraSmallRegular">
                      {errors.globalAllowancePercentageType.message}
                    </Text>
                  )}

                  {/* Custom input field - only show when CUSTOM is selected */}
                  {watchedValues.globalAllowancePercentageType === 'CUSTOM' && (
                    <Flex gap="6px" flexDir="column" mt="10px">
                      <Text color="brand.darkGray" textStyle="extraSmallRegular">
                        Enter custom percentage (0-50)
                      </Text>
                      <Input
                        placeholder="Enter percentage"
                        {...register('globalAllowancePercentageCustom')}
                        onChange={handleCustomGlobalChange}
                        type="number"
                        min="0"
                        max="50"
                        size="lg"
                      />
                      {errors.globalAllowancePercentageCustom && (
                        <Text color="system.error" textStyle="extraSmallRegular">
                          {errors.globalAllowancePercentageCustom.message}
                        </Text>
                      )}
                    </Flex>
                  )}
                </Flex>

                {/* On-demand pay sub-limit section - show if ODP data is available */}
                {shouldShowOdpSection && (
                  <>
                    <Flex gap="10px" flexDir="column">
                      <Text color="brand.mutedPurple" textStyle="body">
                        On-demand pay sub-limit (%) - Current
                      </Text>
                      <Text as="h6" color="brand.mutedPurple" textStyle="h6SemiBold">
                        {getCurrentOdpValue()}
                      </Text>
                    </Flex>
                    <Flex gap="6px" flexDir="column">
                      <Text color="brand.darkGray" textStyle="extraSmallRegular">
                        On-demand pay sub-limit (%) - New
                      </Text>
                      <DropdownInput
                        placeholder="Select a percentage"
                        defaultValue={allowancePercentageOptions.find(
                          (option) => option.value === watchedValues.odpAllowancePercentageType
                        )}
                        options={allowancePercentageOptions}
                        onChange={handleChangeOdpAllowanceType}
                        onBlur={() => {}}
                      />
                      {errors.odpAllowancePercentageType && (
                        <Text color="system.error" textStyle="extraSmallRegular">
                          {errors.odpAllowancePercentageType.message}
                        </Text>
                      )}

                      {/* Custom input field - only show when CUSTOM is selected */}
                      {watchedValues.odpAllowancePercentageType === 'CUSTOM' && (
                        <Flex gap="6px" flexDir="column" mt="10px">
                          <Text color="brand.darkGray" textStyle="extraSmallRegular">
                            Enter custom percentage (0-50)
                          </Text>
                          <Input
                            placeholder="Enter percentage"
                            {...register('odpAllowancePercentageCustom')}
                            onChange={handleCustomOdpChange}
                            type="number"
                            min="0"
                            max="50"
                            size="lg"
                          />
                          {errors.odpAllowancePercentageCustom && (
                            <Text color="system.error" textStyle="extraSmallRegular">
                              {errors.odpAllowancePercentageCustom.message}
                            </Text>
                          )}
                        </Flex>
                      )}
                    </Flex>
                  </>
                )}
              </Stack>
            </ModalBody>
          )}
          <ModalFooter>
            <Button variant="outline" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              rightIcon={<CheckOutline boxSize="20px" />}
              isLoading={isUpdatingAllowancePercentages}
              isDisabled={!isFormValid}
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
}

export default EditSalaryAllowanceModal;
